import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import AutoFixNormalIcon from "@mui/icons-material/AutoFixNormal";
import ChecklistIcon from "@mui/icons-material/Checklist";
import { TestPhase, TestPhaseStep, TestSubType } from "../../../testSpecification/testSpecificationApi";
import TestSubTypeEditor from "../../../testSpecification/TestSubTypeEditor";
import { Localized, useLocalization } from '@fluent/react';
import { showError } from "../../../notifications/notificationsSlice";
import { useAppDispatch } from "../../../../app/hooks";
import { postWithAuth } from "../../../../http";
import { ValidationStepsPrompt } from "../utils/DocumentTestsPrompts";
import { RichTextEditor } from "../../../../app/RichTextEditor";
import { AiGenerationQuestionDialog } from "./AiGenerationQuestionDialog";

interface DocumentTestPhaseTabProps {
  phase: TestPhase;
  onUpdateSteps: (updatedSteps: TestPhaseStep[]) => void;
  onUpdatePhaseSubType: (subType: TestSubType) => void;
  editable: boolean;
  documentId: number;
  testName: string;
  testDescription?: string;
  testMethodology?: string;
}

const DocumentTestPhaseTab: React.FC<DocumentTestPhaseTabProps> = ({
  phase,
  onUpdateSteps,
  onUpdatePhaseSubType,
  editable,
  documentId,
  testName,
  testDescription,
  testMethodology
}) => {
  const dispatch = useAppDispatch();
  const { l10n } = useLocalization();

  const initialSteps = phase?.steps ?? [];
  const [steps, setSteps] = useState<TestPhaseStep[]>(initialSteps);
  const [selectedSubType, setSelectedSubType] = useState<TestSubType>(phase.subType);

  const [selectedStepId, setSelectedStepId] = useState<number | null>(steps[0]?.id ?? null);
  const [generating, setGenerating] = useState(false);
  const [generationQuestion, setGenerationQuestion] = useState<string | null>(null);
  const [generationError, setGenerationError] = useState<string | null>(null);

  const [stepCounter, setStepCounter] = useState(
    Math.max(...(initialSteps.map((s) => s.id)), 0) + 1
  );
  const initialCriteriaIds = initialSteps.flatMap((s) => s.criterias.map((c) => c.id));
  const [criteriaCounter, setCriteriaCounter] = useState(
    Math.max(...(initialCriteriaIds.length ? initialCriteriaIds : [0])) + 1
  );

  const newStepRef = useRef<HTMLDivElement | null>(null);
  const newCriteriaRef = useRef<HTMLInputElement | null>(null);
  const newStepId = useRef<number | null>(null);
  const newCriteriaId = useRef<number | null>(null);

  useEffect(() => {
    if (newStepId.current && newStepRef.current) {
      newStepRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      newStepId.current = null;
    }
    if (newCriteriaId.current && newCriteriaRef.current) {
      newCriteriaRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      newCriteriaId.current = null;
    }
  }, [steps]);

  const selectedStep = useMemo(() => steps.find((s) => s.id === selectedStepId) ?? null, [steps, selectedStepId]);

  const handleSubTypeChange = (newSubType: TestSubType) => {
    setSelectedSubType(newSubType);
    onUpdatePhaseSubType(newSubType);
  };

  const handleAddStep = () => {
    const newStep: TestPhaseStep = {
      id: stepCounter,
      name: "",
      order: steps.length + 1,
      phaseId: phase.id,
      criterias: [],
    };
    const updated = [...steps, newStep];
    setSteps(updated);
    onUpdateSteps(updated);
    setStepCounter((p) => p + 1);
    setSelectedStepId(newStep.id);
    newStepId.current = newStep.id;
  };

  const handleDeleteStep = (id: number) => {
    const updated = steps
      .filter((s) => s.id !== id)
      .map((s, idx) => ({ ...s, order: idx + 1 }));
    setSteps(updated);
    onUpdateSteps(updated);
    if (selectedStepId === id) {
      setSelectedStepId(updated[0]?.id ?? null);
    }
  };

  const handleStepNameChange = (value: string, stepId: number) => {
    const updated = steps.map((s) => (s.id === stepId ? { ...s, name: value } : s));
    setSteps(updated);
    onUpdateSteps(updated);
  };

  const handleAddCriteria = (stepId: number) => {
    const updated = steps.map((s) =>
      s.id === stepId
        ? {
          ...s,
          criterias: [
            ...s.criterias,
            { id: criteriaCounter, name: "", order: s.criterias.length + 1, stepId },
          ],
        }
        : s
    );
    newCriteriaId.current = criteriaCounter;
    setSteps(updated);
    onUpdateSteps(updated);
    setCriteriaCounter((p) => p + 1);
  };

  const handleCriteriaChange = (value: string, stepId: number, criteriaId: number) => {
    const updated = steps.map((s) =>
      s.id === stepId
        ? {
          ...s,
          criterias: s.criterias.map((c) => (c.id === criteriaId ? { ...c, name: value } : c)),
        }
        : s
    );
    setSteps(updated);
    onUpdateSteps(updated);
  };

  const handleDeleteCriteria = (stepId: number, criteriaId: number) => {
    const updated = steps.map((s) =>
      s.id === stepId
        ? {
          ...s,
          criterias: s.criterias
            .filter((c) => c.id !== criteriaId)
            .map((c, idx) => ({ ...c, order: idx + 1 })),
        }
        : s
    );
    setSteps(updated);
    onUpdateSteps(updated);
  };

  const prepareGenerationQuestion = () => {
    if (!testName?.trim()) {
      dispatch(showError("Nazwa testu jest wymagana do generowania kroków."));
      return null;
    }

    return ValidationStepsPrompt.build({
      testName,
      testDescription,
      testMethodology,
      phase,
    });
  };

  const handleOpenGenerationQuestion = () => {
    if (!editable) return;
    const question = prepareGenerationQuestion();
    if (question === null) return;
    setGenerationQuestion(question);
    setGenerationError(null);
  };

  const handleQuickGenerate = () => {
    if (!editable || generating) return;
    const question = prepareGenerationQuestion();
    if (question !== null) void handleGenerate(question, true);
  };

  const handleGenerate = async (question: string, useSystemPrompt = false) => {
    if (generating) return;
    const reportError = (error: string) => {
      if (useSystemPrompt) dispatch(showError(error));
      else setGenerationError(error);
    };
    setGenerating(true);
    setGenerationError(null);
    try {
      const result = await dispatch(postWithAuth({
        url: `api/document-tests/${documentId}/ai/generate`,
        payload: {
          question,
          type: "test-steps",
          testName,
          relatedObjectDescription: testDescription ?? "",
          useSystemPrompt
        },
      }));

      if (postWithAuth.fulfilled.match(result)) {
        const { payload } = result;

        try {
          const parsed = JSON.parse(payload.answer);

          if (!Array.isArray(parsed)) {
            reportError("validation-ai-error");
            return;
          }

          // oczekujemy: [{ description: string, acceptanceCriteria: string[] }]
          let ci: number = 1
          const generatedSteps: TestPhaseStep[] = parsed.map((s: any, index: number) => {
            if (
              typeof s.description !== "string" ||
              !Array.isArray(s.acceptanceCriteria) ||
              !s.acceptanceCriteria.every((c: any) => typeof c === "string")
            ) {
              throw new Error("validation-ai-error");
            }

            const criterias = (s.acceptanceCriteria as string[]).map((c, innerIdx) => ({
              id: ci++,
              name: c,
              order: innerIdx + 1,
              stepId: 0, // backend i tak nadpisze przy zapisie
            }));

            return {
              id: index + 1,
              name: s.description,
              order: index + 1,
              phaseId: phase.id,
              criterias,
            };
          });

          // ustawiamy kroki lokalnie + informujemy rodzica
          setSteps(generatedSteps);
          onUpdateSteps(generatedSteps);

          // aktualizacja liczników ID
          const maxStepId = generatedSteps.length
            ? Math.max(...generatedSteps.map((s) => s.id))
            : 0;
          setStepCounter(maxStepId + 1);

          const allCritIds = generatedSteps.flatMap((s) => s.criterias.map((c) => c.id));
          const maxCritId = allCritIds.length ? Math.max(...allCritIds) : 0;
          setCriteriaCounter(maxCritId + 1);

          // zaznacz pierwszy krok
          if (generatedSteps.length > 0) {
            setSelectedStepId(generatedSteps[0].id);
          }
          setGenerationQuestion(null);
        } catch (e) {
          reportError("validation-ai-error");
        }
      } else {
        const httpPayload = result.payload;
        if (httpPayload?.kind === "http") {
          reportError(httpPayload.problem?.title ?? "error");
        } else {
          reportError("error");
        }
      }
    } catch {
      reportError("error");
    } finally {
      setGenerating(false);
    }
  };



  return (
    <>
    <Paper sx={{ minWidth: 0 }}>
      <Box p={2}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <TestSubTypeEditor subType={selectedSubType} onUpdatedSubType={handleSubTypeChange} />
          <Box display="flex" alignItems="center">
          <Tooltip title={l10n.getString("test-edit-ai-question", null, "_Edit AI question")}>
            <span>
              <IconButton
                disabled={!editable || generating}
                color="primary"
                onClick={handleOpenGenerationQuestion}
                size="small"
              >
                <AutoFixHighIcon
                  sx={{
                    "@keyframes spin": {
                      "0%": { transform: "rotate(0deg)" },
                      "100%": { transform: "rotate(360deg)" },
                    },
                    animation: generating ? "spin 0.8s linear infinite" : "none",
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={l10n.getString("test-generate-system-prompt", null, "_Generate with system prompt")}>
            <span>
              <IconButton
                disabled={!editable || generating}
                color="secondary"
                onClick={handleQuickGenerate}
                size="small"
              >
                <AutoFixNormalIcon />
              </IconButton>
            </span>
          </Tooltip>
          </Box>

        </Box>

        <Grid container spacing={2} sx={{ height: '100%' }}>
          <Grid
            item
            xs={12}
            sx={{
              minWidth: 0,
              flexBasis: { xs: '100%', md: '40%' },
              maxWidth: { xs: '100%', md: '40%' },
            }}
          >
            <Paper variant="outlined" sx={{ overflow: "hidden" }}>
              <Box px={1.5} py={1} bgcolor={(t) => t.palette.grey[100]} borderBottom={(t) => `1px solid ${t.palette.divider}`}>
                <Typography variant="body1" fontWeight={700}>
                  {phase.phaseType} — {<Localized id="test-steps">
                    <span>_test-steps</span>
                  </Localized>} ({steps.length})
                </Typography>
              </Box>
              <Box px={1.5} py={0.5} borderBottom={(t) => `1px solid ${t.palette.divider}`}>
                <Grid container alignItems="center" spacing={1}>
                  <Grid item xs={2}>
                    <Typography variant="caption" color="text.secondary">
                      <Localized id="lp">
                        <span>_Lp.</span>
                      </Localized>
                    </Typography>
                  </Grid>
                  <Grid item xs={7} sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary">
                      <Localized id="name">
                        <span>_name.</span>
                      </Localized>
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="caption" color="text.secondary">
                      <Localized id="validation-acceptance-criteria">
                        <span>_Kryteria.</span>
                      </Localized>
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <List dense disablePadding>
                {steps.map((step) => (
                  <ListItem key={step.id} disableGutters sx={{ borderBottom: (t) => `1px solid ${t.palette.divider}` }}>
                    <ListItemButton
                      selected={selectedStepId === step.id}
                      onClick={() => setSelectedStepId(step.id)}
                      sx={{ py: 1, px: 1.5 }}
                    >
                      <Grid container alignItems="center" spacing={1}>
                        <Grid item xs={2}>
                          <Typography variant="body2" color="text.secondary">{step.order}.</Typography>
                        </Grid>
                        <Grid item xs={7} sx={{ minWidth: 0 }}>
                          <ListItemText
                            primary={
                              step.name ? (
                                <Typography
                                  variant="body2"
                                  component="div"
                                  sx={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    [`& p`]: { m: 0, display: "inline" },
                                  }}
                                  dangerouslySetInnerHTML={{ __html: step.name }}
                                />
                              ) : (
                                <Typography variant="body2" noWrap>
                                  <em style={{ color: '#888' }}>
                                    <Localized id="test-no-step-description-warning">
                                      <span>_Brak opisu…</span>
                                    </Localized>
                                  </em>
                                </Typography>
                              )
                            }
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <Chip size="small" icon={<ChecklistIcon />} label={String(step.criterias.length)} />
                        </Grid>
                      </Grid>
                    </ListItemButton>
                    <Tooltip title={<Localized id="test-delete-step">
                      <span>_Usun krok.</span>
                    </Localized>}>
                      <IconButton disabled={!editable} size="small" onClick={() => handleDeleteStep(step.id)} sx={{ mr: 1, '&:hover': { color: 'red' } }}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </ListItem>
                ))}
                {steps.length === 0 && (
                  <Box p={2} textAlign="center" color="text.secondary">

                    <Localized id="test-no-steps-warning">
                      <span>_ Brak kroków — dodaj pierwszy.</span>
                    </Localized>
                  </Box>
                )}
              </List>

              <Divider />
              <Box p={1.5} display="flex" justifyContent="center">
                <Button startIcon={<AddCircleOutlineIcon />} disabled={!editable} variant="contained" color="success" onClick={handleAddStep}>
                  <Localized id="test-add-step">
                    <span>_Dodaj krok</span>
                  </Localized>
                </Button>
              </Box>
            </Paper>
          </Grid>
          <Grid
            item
            xs={12}
            sx={{
              minWidth: 0,
              flexBasis: { xs: '100%', md: '60%' },
              maxWidth: { xs: '100%', md: '60%' },
            }}
          >
            <Paper variant="outlined" >
              <Box p={2}>
                {!selectedStep && (
                  <Typography color="text.secondary"
                  >
                    <Localized id="test-choose-step-to-edit">
                      <span>_Wybierz krok po lewej, aby edytować.</span>
                    </Localized>
                  </Typography>
                )}
                {selectedStep && (
                  <Stack spacing={2}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Typography
                        variant="subtitle1"
                        fontWeight={700}
                        noWrap
                        sx={{ width: 80 }}
                      >
                        <Localized id="test-step">
                          <span>_step.</span>
                        </Localized>
                        {selectedStep.order}
                      </Typography>
                      <Box
                        sx={{ flexGrow: 1 }}
                        ref={newStepId.current === selectedStep.id ? newStepRef : undefined}
                      >
                        <RichTextEditor
                          key={selectedStep.id}
                          value={selectedStep.name}
                          onValueChange={val => handleStepNameChange(val, selectedStep.id)}
                          label={<Localized id="test-step-description"></Localized>}
                          editable={editable}
                        />
                      </Box>
                    </Box>

                    <Divider />

                    <Box>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                        <Typography variant="subtitle2">
                          <Localized id="validation-acceptance-criteria">
                            <span>_Kryteria.</span>
                          </Localized>
                          ({selectedStep.criterias.length})</Typography>
                        <Button
                          size="small"
                          startIcon={<AddCircleOutlineIcon />}
                          variant="outlined"
                          onClick={() => handleAddCriteria(selectedStep.id)}
                          disabled={!editable}
                        >
                          <Localized id="test-add-criteria">
                            <span>_Dodaj kryterium</span>
                          </Localized>
                        </Button>
                      </Box>

                      <Stack spacing={1.25}>
                        {selectedStep.criterias.map((c) => (
                          <Box
                            key={c.id}
                            display="flex"
                            alignItems="center"
                            gap={1}
                            sx={{
                                p: 1,
                                borderRadius: 1,
                                border: "1px solid",
                                borderColor: "divider",
                                bgcolor: "background.paper",
                            }}
                          >
                            <Typography variant="body2" width={44} textAlign="right" color="text.secondary">
                              {selectedStep.order}.{c.order}
                            </Typography>
                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                              <RichTextEditor
                                  value={c.name}
                                  onValueChange={val => handleCriteriaChange(val, selectedStep.id, c.id)}
                                  label={<Localized id="test-criteria-description"></Localized>}
                                  editable={editable}
                              />
                            </Box>
                            <Box sx={{ width: 44, flexShrink: 0, display: "flex", justifyContent: "center" }}>
                              <Tooltip title={<Localized id="delete"><span>_Usuń</span></Localized>}>
                                <IconButton disabled={!editable} size="small" onClick={() => handleDeleteCriteria(selectedStep.id, c.id)} sx={{ '&:hover': { color: 'red' } }}>
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                        ))}
                        {selectedStep.criterias.length === 0 && (
                          <Typography color="text.secondary" variant="body2">
                            <Localized id="test-no-criteria-warning">
                              <span>_Brak kryteriów — dodaj pierwsze.</span>
                            </Localized>
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                  </Stack>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Paper>
    {generationQuestion !== null && (
      <AiGenerationQuestionDialog
        titleId="test-generate-steps"
        question={generationQuestion}
        generating={generating}
        error={generationError}
        onDismissError={() => setGenerationError(null)}
        onClose={() => setGenerationQuestion(null)}
        onGenerate={handleGenerate}
      />
    )}
    </>
  );
};


export default DocumentTestPhaseTab;
